import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CalendarIcon, Globe, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { EditableDropdown } from '@/components/ui/editable-dropdown'
import { useClientsList } from '@/hooks/useClients'

interface DomainAddDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDomainAdded: () => void
}

interface DomainFormData {
  domain: string
  client_name: string
  status: string
  domain_registrar: string
  mailbox_provider: string
  email_provider: string
  purchase_date: Date | undefined
  renewal_date: Date | undefined
  expiry_date: Date | undefined
  auto_renewal: boolean
  cost: string
  notes: string
}

export function DomainAddDialog({ open, onOpenChange, onDomainAdded }: DomainAddDialogProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<DomainFormData>({
    domain: '',
    client_name: '',
    status: 'Active',
    domain_registrar: '',
    mailbox_provider: '',
    email_provider: '',
    purchase_date: undefined,
    renewal_date: undefined,
    expiry_date: undefined,
    auto_renewal: false,
    cost: '',
    notes: ''
  })

  const { data: clientsList = [] } = useClientsList()
  const clientOptions = React.useMemo(() => {
    const unique = new Set<string>()
    clientsList.forEach(client => {
      if (client?.name) {
        unique.add(client.name)
      }
    })
    if (formData.client_name) {
      unique.add(formData.client_name)
    }
    return Array.from(unique)
      .filter(name => !!name && name.trim().length > 0)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ value: name, label: name }))
  }, [clientsList, formData.client_name])

  const statusOptions = ['Active', 'Inactive', 'Expired', 'Suspended', 'Deactivated']
  const registrarOptions = ['Hosting.de J-account', 'Hosting.de K-account', 'Hosting.de', 'Cloudflare', 'GoDaddy', 'Namecheap', 'Spaceship', 'InboxKit']
  const mailboxProviderOptions = ['Superwave', 'Maildoso', 'Cheap Inboxes', 'Primary', 'Google Workspace', 'Microsoft 365', 'InboxKit']
  const emailProviderOptions = ['Google', 'Microsoft', 'Cloudflare']

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.domain.trim()) {
      toast({
        title: "Domain Required",
        description: "Please enter a domain name",
        variant: "destructive"
      })
      return
    }

    setIsSubmitting(true)

    try {
      const domainData = {
        domain: formData.domain.trim().toLowerCase(),
        client_name: formData.client_name ? formData.client_name.trim() : null,
        status: formData.status,
        domain_registrar: formData.domain_registrar || null,
        mailbox_provider: formData.mailbox_provider || null,
        email_provider: formData.email_provider || null,
        purchase_date: formData.purchase_date ? format(formData.purchase_date, 'yyyy-MM-dd') : null,
        renewal_date: formData.renewal_date ? format(formData.renewal_date, 'yyyy-MM-dd') : null,
        expiry_date: formData.expiry_date ? format(formData.expiry_date, 'yyyy-MM-dd') : null,
        auto_renewal: formData.auto_renewal,
        // Note: cost field removed - not in database schema. Store in notes if needed.
        notes: formData.cost 
          ? (formData.notes ? `${formData.notes}\nCost: ${formData.cost}` : `Cost: ${formData.cost}`)
          : formData.notes || null,
        total_mailboxes: 0,
        active_mailboxes: 0,
        total_emails_sent: 0,
        total_email_replies: 0,
        total_email_bounces: 0,
      }

      // Use upsert to insert new or update existing domain
      const { data, error } = await supabase
        .from('domains')
        .upsert([domainData], {
          onConflict: 'domain',
          ignoreDuplicates: false // Update existing domain instead of ignoring
        })
        .select()

      if (error) {
        throw error
      }

      // Check if domain was newly created or updated
      const wasNew = data && data[0] && data[0].created_at === data[0].updated_at
      const action = wasNew ? 'added' : 'updated'

      toast({
        title: wasNew ? "Domain Added" : "Domain Updated",
        description: `Successfully ${action} domain: ${formData.domain}`,
      })

      // Reset form
      setFormData({
        domain: '',
        client_name: '',
        status: 'Active',
        domain_registrar: '',
        mailbox_provider: '',
        email_provider: '',
        purchase_date: undefined,
        renewal_date: undefined,
        expiry_date: undefined,
        auto_renewal: false,
        cost: '',
        notes: ''
      })

      onDomainAdded()
      onOpenChange(false)

    } catch (error: any) {
      console.error('Error adding domain:', error)
      const errorMessage = error?.message || error?.error?.message || 'Failed to add domain. Please check the console for details.'
      toast({
        title: "Failed to add domain",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: keyof DomainFormData, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value }
      
      // Auto-populate renewal and expiry dates when purchase date changes
      if (field === 'purchase_date' && value) {
        const purchaseDate = new Date(value)
        
        // Set renewal date to exactly 365 days after purchase date
        const renewalDate = new Date(purchaseDate)
        renewalDate.setDate(renewalDate.getDate() + 365) // Add exactly 365 days
        
        // Set expiry date to 1 day before renewal date
        const expiryDate = new Date(renewalDate)
        expiryDate.setDate(expiryDate.getDate() - 1)
        
        updated.renewal_date = renewalDate
        updated.expiry_date = expiryDate
      }
      
      return updated
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Add New Domain
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Domain Name - Required */}
          <div className="space-y-2">
            <Label htmlFor="domain" className="text-sm font-medium">
              Domain Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="domain"
              placeholder="example.com"
              value={formData.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
              required
            />
          </div>

          {/* Client Assignment */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Client</Label>
            <EditableDropdown
              value={formData.client_name}
              onChange={(value) => handleInputChange('client_name', value)}
              options={clientOptions}
              placeholder="Select or add a client"
              className="w-full"
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status" className="text-sm font-medium">Status</Label>
            <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Provider Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="domain_registrar" className="text-sm font-medium">Domain Registrar</Label>
              <EditableDropdown
                value={formData.domain_registrar}
                onChange={(value) => handleInputChange('domain_registrar', value)}
                options={registrarOptions}
                placeholder="Select or type registrar"
                className="w-full"
                allowCreate={true}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mailbox_provider" className="text-sm font-medium">Mailbox Provider</Label>
              <Select value={formData.mailbox_provider} onValueChange={(value) => handleInputChange('mailbox_provider', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {mailboxProviderOptions.map(provider => (
                    <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email_provider" className="text-sm font-medium">Email Provider</Label>
              <Select value={formData.email_provider} onValueChange={(value) => handleInputChange('email_provider', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {emailProviderOptions.map(provider => (
                    <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Purchase Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.purchase_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.purchase_date ? format(formData.purchase_date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.purchase_date}
                    onSelect={(date) => handleInputChange('purchase_date', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Renewal Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.renewal_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.renewal_date ? format(formData.renewal_date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.renewal_date}
                    onSelect={(date) => handleInputChange('renewal_date', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Expiry Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.expiry_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.expiry_date ? format(formData.expiry_date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.expiry_date}
                    onSelect={(date) => handleInputChange('expiry_date', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Additional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cost" className="text-sm font-medium">Cost</Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.cost}
                onChange={(e) => handleInputChange('cost', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Auto Renewal</Label>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.auto_renewal}
                  onCheckedChange={(checked) => handleInputChange('auto_renewal', checked)}
                />
                <span className="text-sm">{formData.auto_renewal ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes about this domain..."
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding Domain...
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4 mr-2" />
                  Add Domain
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}